import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Eye, CheckCircle,
  Crown, Star, Rocket, Award, Zap, Trophy, Medal, 
  Gem, Diamond, Sparkles, Flame, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Users, UserCheck, Cake, User, MapPin, Navigation
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { mockTrainers } from '../mockData';
import Footer from '../components/Footer';
import ResponsiveHeader from '../components/ResponsiveHeader';
import AdvancedFilterModal from '../components/AdvancedFilterModal';
import { useAuth } from '../context/AuthContext';
import StarRating from '../components/StarRating';
import HomepageAdSidebar from '../components/HomepageAdSidebar';
import HomepageBannerAd from '../components/HomepageBannerAd';
import LocationSearchBox from '../components/LocationSearchBox';

// Icon mapping for dynamic rendering from boost categories
const BoostIconMap = {
  Crown, Star, Rocket, Award, Zap, Trophy, Medal,
  Gem, Diamond, Sparkles, Fire: Flame, Bolt: Zap, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, Verified: BadgeCheck, CheckCircle
};

const HomePage = () => {
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [proficiencyIcons, setProficiencyIcons] = useState({});
  const [sportCategories, setSportCategories] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [pendingFeedback, setPendingFeedback] = useState(null);
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(true);
  
  // Location search state
  const [userLocation, setUserLocation] = useState(null);
  const [searchDistance, setSearchDistance] = useState(25); // Default 25km
  const [locationSearchActive, setLocationSearchActive] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const trainersPerPage = 12; // 3x4 grid
  const trainersGridRef = useRef(null); // Ref for smooth scroll to trainers section
  const isFirstRender = useRef(true); // Track if it's first render to avoid scroll on mount
  const [siteSettings, setSiteSettings] = useState({});
  const [highlightedTrainerId, setHighlightedTrainerId] = useState(null);
  const [boostCategories, setBoostCategories] = useState({});
  const [trainerAchievements, setTrainerAchievements] = useState({});

  // Scroll to top when component mounts - MUST be first
  useEffect(() => {
    // Only scroll if not already at top (to avoid breaking smooth scroll from logo click)
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    
    
    if (currentScroll > 10) {
      window.scrollTo({ 
        top: 0, 
        left: 0, 
        behavior: 'smooth' 
      });
    } else {
    }
  }, []);

  useEffect(() => {
    fetchTrainers();
    fetchPendingFeedback();
    fetchProficiencyIcons();
    fetchSiteSettings();
    fetchBoostCategories();
  }, []);

  const fetchBoostCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/boost-categories`);
      if (response.ok) {
        const data = await response.json();
        // Create a map: key -> category info
        const categoryMap = {};
        (data.categories || []).forEach(cat => {
          categoryMap[cat.key] = {
            label: cat.label,
            icon: cat.icon,
            color: cat.color,
            description: cat.description
          };
        });
        setBoostCategories(categoryMap);
      }
    } catch (error) {
      console.error('[HOMEPAGE] Error fetching boost categories:', error);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/site-settings`);
      if (response.ok) {
        const settings = await response.json();
        setSiteSettings(settings);
        // Update document title
        if (settings.site_title) {
          document.title = settings.site_title;
        }
      }
    } catch (error) {
      console.error('[HOMEPAGE] Error fetching site settings:', error);
    }
  };

  const fetchProficiencyIcons = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/proficiency-icons`);
      if (response.ok) {
        const icons = await response.json();
        
        // Create a map: name -> icon_url
        const iconMap = {};
        icons.forEach(icon => {
          iconMap[icon.name.toLowerCase()] = icon.icon_url;
        });
        setProficiencyIcons(iconMap);
        
        // Set sport categories from API
        const categories = icons.map((icon, index) => ({
          id: icon.id || index,
          name: icon.name,
          icon: icon.icon_url,
          order: icon.order || 0
        }));
        setSportCategories(categories);
        
      }
    } catch (error) {
      console.error('[HOMEPAGE] Error fetching proficiency icons:', error);
    }
  };

  useEffect(() => {
    if (user && user.role === 'trainer') {
      // Check if user has dismissed the banner for this notification
      const dismissedFeedbackId = localStorage.getItem('dismissedFeedbackBanner');
      fetchPendingFeedback(dismissedFeedbackId);
    }
  }, [user]);

  const fetchPendingFeedback = async (dismissedId = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const notifications = await response.json();
        const changeRequest = notifications.find(
          n => n.type === 'listing_changes_requested' && 
               !n.read &&
               n.id !== dismissedId  // Don't show if dismissed on homepage
        );
        
        if (changeRequest) {
          setPendingFeedback(changeRequest);
          setShowFeedbackBanner(true);
        } else {
          // No unread feedback - clear localStorage if it was previously dismissed
          const storedId = localStorage.getItem('dismissedFeedbackBanner');
          if (storedId) {
            localStorage.removeItem('dismissedFeedbackBanner');
          }
          setPendingFeedback(null);
          setShowFeedbackBanner(false);
        }
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const handleDismissFeedback = () => {
    if (pendingFeedback && pendingFeedback.id) {
      localStorage.setItem('dismissedFeedbackBanner', pendingFeedback.id);
    }
    setShowFeedbackBanner(false);
  };


  // Fetch trainers from backend
  const fetchTrainers = async (locationParams = null, retryCount = 0) => {
    try {
      setLoading(true);
      setFetchError(false);
      
      // Build URL with location parameters if provided
      let url = `${API_BASE}/api/trainers?limit=100`;
      
      if (locationParams) {
        if (locationParams.lat && locationParams.lng) {
          url += `&lat=${locationParams.lat}&lng=${locationParams.lng}`;
        }
        if (locationParams.distance) {
          url += `&distance=${locationParams.distance}`;
        }
        if (locationParams.city) {
          url += `&city=${encodeURIComponent(locationParams.city)}`;
        }
        if (locationParams.province) {
          url += `&province=${encodeURIComponent(locationParams.province)}`;
        }
      }
      
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        
        const trainersArray = data.trainers || data;  // Handle both paginated and direct response
        
        // Filter only active trainers with listing_status = 'listed' OR status = 'active'
        // Show trainers that are either: 
        // 1. Admin approved (status = 'active') OR
        // 2. Explicitly listed (listing_status = 'listed')
        const activeTrainers = trainersArray.filter(trainer => 
          trainer.status === 'active' && 
          (trainer.listing_status === 'listed' || !trainer.listing_status || trainer.listing_status === 'draft')
        );
        
        
        // Transform data to match frontend structure
        const transformedTrainers = activeTrainers.map(trainer => {
          // Helper to get full photo URL
          const getFullPhotoUrl = (photoPath) => {
            if (!photoPath) return null;
            if (photoPath.startsWith('http')) return photoPath;
            // Convert relative path to full URL
            return `${API_BASE}${photoPath}`;
          };
          
          return {
            id: trainer.id || trainer._id,
            name: trainer.name || 'Unknown Trainer',  // Backend returns 'name' from user
            specializations: trainer.specializations || [],
            location: {
              city: trainer.location?.city || '',
              province: trainer.location?.province || '',
              coordinates: trainer.location?.coordinates || null,
              formatted_address: trainer.location?.formatted_address || ''
            },
            distance_km: trainer.distance_km || null,  // Distance from search location
            pricePerHour: trainer.pricePerHour || trainer.price_per_hour || 0,
            rating: trainer.rating || 0,
            review_count: trainer.review_count || 0,
            photos: (trainer.photos || []).map(getFullPhotoUrl).filter(Boolean),
            bio: trainer.bio || '',
            slug: trainer.slug || trainer.id || trainer._id,
            status: trainer.status,  // Preserve status for filtering
            membership_active: trainer.membership_active || false,  // Membership status
            membership_type: trainer.membership_type || null,  // Membership type (boosted/listing)
            training_types: trainer.training_types || [],  // Training types
            gender: trainer.gender || null,  // Gender
            age: trainer.age || null,  // Age for filtering
            profile_views: trainer.profile_views || 0,  // Profile views count
            achievement_badges: trainer.achievement_badges || [],  // Achievement badges
            availability_summary: trainer.availability_summary || null  // Availability summary
          };
        });
        
        setTrainers(transformedTrainers);
        setLoading(false);
      } else {
        throw new Error(`Response not OK: ${response.status}`);
      }
    } catch (error) {
      console.error('[HOMEPAGE] Error fetching trainers:', error);
      if (retryCount < 2) {
        setTimeout(() => fetchTrainers(locationParams, retryCount + 1), 1500 * (retryCount + 1));
        return;
      }
      setFetchError(true);
      setLoading(false);
    }
  };

  const applyAdvancedFilters = (filters) => {
    setAppliedFilters(filters);
    
    // Clear previous highlight
    setHighlightedTrainerId(null);
    
    // Wait for filters to apply, then find matching trainers
    setTimeout(() => {
      // Filter trainers based on new filters
      const matchedTrainers = trainers.filter(trainer => {
        // Apply same filter logic as filteredTrainers
        const matchesTrainerName = !filters.trainerName || 
          filters.trainerName === '' ||
          trainer.name.toLowerCase().includes(filters.trainerName.toLowerCase());
        
        const matchesCity = !filters.city || 
          filters.city === '' ||
          (trainer.location && trainer.location.city && 
           trainer.location.city.toLowerCase() === filters.city.toLowerCase());
        
        const matchesLocation = !filters.location || 
          filters.location === '' ||
          (trainer.location && (
            (trainer.location.city && trainer.location.city.toLowerCase().includes(filters.location.toLowerCase())) ||
            (trainer.location.province && trainer.location.province.toLowerCase().includes(filters.location.toLowerCase()))
          ));
        
        const matchesProficiency = !filters.proficiency || 
          filters.proficiency === '' ||
          (trainer.specializations && Array.isArray(trainer.specializations) &&
           trainer.specializations.some(s => s.toLowerCase().includes(filters.proficiency.toLowerCase())));
        
        const matchesPrice = !filters.priceRange || 
          (trainer.pricePerHour >= filters.priceRange[0] && 
           trainer.pricePerHour <= filters.priceRange[1]);
        
        const isDefaultAgeRange = filters.ageRange && 
          Array.isArray(filters.ageRange) && 
          filters.ageRange.length === 2 && 
          filters.ageRange[0] === 18 && 
          filters.ageRange[1] === 70;
        
        const matchesAge = !filters.ageRange || 
          !Array.isArray(filters.ageRange) || 
          filters.ageRange.length !== 2 ||
          isDefaultAgeRange ||
          !trainer.age ||
          (typeof trainer.age === 'number' &&
           trainer.age >= filters.ageRange[0] && 
           trainer.age <= filters.ageRange[1]);
        
        const matchesSessionType = !filters.sessionType || 
          filters.sessionType === '' ||
          !trainer.training_type && (!trainer.training_types || trainer.training_types.length === 0) ||
          (trainer.training_type && trainer.training_type === filters.sessionType) ||
          (trainer.training_types && Array.isArray(trainer.training_types) && 
           trainer.training_types.includes(filters.sessionType));
        
        const matchesGender = !filters.gender || 
          filters.gender === '' ||
          filters.gender === 'Any' ||
          !trainer.gender ||
          (trainer.gender && (
            trainer.gender.toLowerCase() === filters.gender.toLowerCase() ||
            (filters.gender === 'X (non-binary)' && trainer.gender.toLowerCase() === 'non-binary')
          ));
        
        return matchesTrainerName && matchesCity && matchesLocation && 
               matchesProficiency && matchesPrice && matchesAge && 
               matchesSessionType && matchesGender && trainer.status === 'active';
      });
      
      if (matchedTrainers.length === 0) {
        // No matches - do nothing
        return;
      }
      
      // Highlight ALL matching trainers
      const matchingIds = matchedTrainers.map(t => String(t.id));
      
      setTimeout(() => {
        setHighlightedTrainerId(matchingIds); // Set as array
        
        // Scroll to FIRST matching trainer
        const firstTrainerId = matchingIds[0];
        setTimeout(() => {
          const element = document.getElementById(`trainer-${firstTrainerId}`);
          if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top - (window.innerHeight / 2) + (rect.height / 2);
            window.scrollTo({ top: scrollTop, behavior: 'smooth' });
          }
        }, 300);
        
        // Remove highlight after 5 seconds
        setTimeout(() => {
          setHighlightedTrainerId(null);
        }, 5000);
      }, 100);
    }, 200);
  };

  const filteredTrainers = trainers.filter(trainer => {
    // Basic search with null checks - if searchTerm is empty, show all trainers
    const matchesSearch = !searchTerm || searchTerm === '' ||
      (trainer.name && trainer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (trainer.location && trainer.location.city && trainer.location.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (trainer.specializations && Array.isArray(trainer.specializations) && 
       trainer.specializations.some(s => s.toLowerCase().includes(searchTerm.toLowerCase())));
    
    // Category filter with null checks
    const matchesCategory = !selectedCategory || 
      (trainer.specializations && Array.isArray(trainer.specializations) &&
       trainer.specializations.some(s => s.toLowerCase() === selectedCategory.toLowerCase()));
    
    // Advanced filters
    const matchesTrainerName = !appliedFilters.trainerName || 
      appliedFilters.trainerName === '' ||  // Empty string means no filter
      trainer.name.toLowerCase().includes(appliedFilters.trainerName.toLowerCase());
    
    const matchesCity = !appliedFilters.city || 
      appliedFilters.city === '' ||  // Empty string means no filter
      (trainer.location && trainer.location.city && 
       trainer.location.city.toLowerCase() === appliedFilters.city.toLowerCase());
    
    const matchesLocation = !appliedFilters.location || 
      appliedFilters.location === '' ||  // Empty string means no filter
      (trainer.location && (
        (trainer.location.city && trainer.location.city.toLowerCase().includes(appliedFilters.location.toLowerCase())) ||
        (trainer.location.province && trainer.location.province.toLowerCase().includes(appliedFilters.location.toLowerCase()))
      ));
    
    const matchesProficiency = !appliedFilters.proficiency || 
      appliedFilters.proficiency === '' ||  // Empty string means no filter
      (trainer.specializations && Array.isArray(trainer.specializations) &&
       trainer.specializations.some(s => s.toLowerCase().includes(appliedFilters.proficiency.toLowerCase())));
    
    const matchesPrice = !appliedFilters.priceRange || 
      (trainer.pricePerHour >= appliedFilters.priceRange[0] && 
       trainer.pricePerHour <= appliedFilters.priceRange[1]);
    
    // Age Range filter
    // If no ageRange provided, invalid, or is the default [18,70], show all trainers
    // Trainers without age info should also be included (not filtered out)
    const isDefaultAgeRange = appliedFilters.ageRange && 
      Array.isArray(appliedFilters.ageRange) && 
      appliedFilters.ageRange.length === 2 && 
      appliedFilters.ageRange[0] === 18 && 
      appliedFilters.ageRange[1] === 70;
    
    const matchesAge = !appliedFilters.ageRange || 
      !Array.isArray(appliedFilters.ageRange) || 
      appliedFilters.ageRange.length !== 2 ||
      isDefaultAgeRange ||  // Treat default range as "no filter"
      !trainer.age ||  // Include trainers without age info
      (typeof trainer.age === 'number' &&
       trainer.age >= appliedFilters.ageRange[0] && 
       trainer.age <= appliedFilters.ageRange[1]);
    
    // Session Type filter (Online, In Person, Both)
    // Check both training_type (singular) and training_types (array)
    // Include trainers without training type info
    const matchesSessionType = !appliedFilters.sessionType || 
      appliedFilters.sessionType === '' ||  // Empty string means no filter
      !trainer.training_type && (!trainer.training_types || trainer.training_types.length === 0) ||  // No training type info
      (trainer.training_type && trainer.training_type === appliedFilters.sessionType) ||
      (trainer.training_types && Array.isArray(trainer.training_types) && 
       trainer.training_types.includes(appliedFilters.sessionType));
    
    // Gender filter
    // Handle "X (non-binary)" display value -> "non-binary" database value
    // Include trainers without gender info
    const matchesGender = !appliedFilters.gender || 
      appliedFilters.gender === '' ||  // Empty string means no filter
      appliedFilters.gender === 'Any' ||
      !trainer.gender ||  // Include trainers without gender info
      (trainer.gender && (
        trainer.gender.toLowerCase() === appliedFilters.gender.toLowerCase() ||
        (appliedFilters.gender === 'X (non-binary)' && trainer.gender.toLowerCase() === 'non-binary')
      ));
    
    return matchesSearch && matchesCategory && matchesTrainerName && 
           matchesCity && matchesLocation && matchesProficiency && 
           matchesPrice && matchesAge && matchesSessionType && matchesGender && 
           trainer.status === 'active';
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredTrainers.length / trainersPerPage);
  const startIndex = (currentPage - 1) * trainersPerPage;
  const endIndex = startIndex + trainersPerPage;
  const paginatedTrainers = filteredTrainers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters, searchTerm, selectedCategory]);

  // Custom smooth scroll with visible animation (1.5 seconds)
  const animateScroll = (targetPosition) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 1500; // 1.5 seconds for noticeable animation
    let startTime = null;

    // Ease-out cubic for smooth deceleration
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      window.scrollTo(0, startPosition + distance * easeOutCubic(progress));
      
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  // Smooth scroll to trainers section when page changes (not on first render)
  useEffect(() => {
    
    // Skip scroll on first render (page load)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Smooth scroll to trainers grid when pagination changes
    if (trainersGridRef.current) {
      const targetElement = trainersGridRef.current;
      const headerOffset = 80;
      const currentScrollY = window.pageYOffset;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const targetScrollPosition = Math.max(0, elementPosition + currentScrollY - headerOffset);
      
      // Use custom animation for more visible effect
      animateScroll(targetScrollPosition);
      
    } else {
    }
  }, [currentPage]);

  const nextImage = (trainerId, photoCount) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [trainerId]: ((prev[trainerId] || 0) + 1) % photoCount
    }));
  };

  const prevImage = (trainerId, photoCount) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [trainerId]: ((prev[trainerId] || 0) - 1 + photoCount) % photoCount
    }));
  };

  const resetFilters = () => {
    setSelectedCategory(null);
    setSearchTerm('');
    setAppliedFilters({});
    setUserLocation(null);
    setLocationSearchActive(false);
    fetchTrainers(); // Reset to all trainers
  };

  const handleSearchClick = () => {
    setIsFilterModalOpen(true);
  };

  // Handle location search - Navigate to /search page with location params
  const handleLocationSearch = ({ location, distance }) => {
    if (location && location.coordinates) {
      // Navigate to /search page with complete location parameters
      const params = new URLSearchParams({
        lat: location.coordinates.lat,
        lng: location.coordinates.lng,
        radius: distance || 25,
        city: location.city || location.name || '',
        province: location.province || ''
      });
      navigate(`/search?${params.toString()}`);
    }
  };

  const handleLocationChange = (location) => {
    setUserLocation(location);
  };

  const handleDistanceChange = (distance) => {
    setSearchDistance(distance);
    
    // If we have a location, refetch with new distance
    if (userLocation?.coordinates) {
      fetchTrainers({
        lat: userLocation.coordinates.lat,
        lng: userLocation.coordinates.lng,
        distance: distance,
        city: userLocation.city,
        province: userLocation.province
      });
    }
  };

  const clearLocationSearch = () => {
    setUserLocation(null);
    setLocationSearchActive(false);
    fetchTrainers();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-red-800">
      <ResponsiveHeader 
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        onReset={resetFilters}
        onSearchClick={handleSearchClick}
      />

      {/* Location Search Section */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Find Personal Trainers Near You
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Search by location to find trainers in your area
          </p>
        </div>
        
        <LocationSearchBox
          onLocationChange={handleLocationChange}
          onDistanceChange={handleDistanceChange}
          onSearch={handleLocationSearch}
          defaultDistance={searchDistance}
          showDistanceFilter={true}
          placeholder="Enter city or address..."
        />
        
        {/* Active Location Filter Badge */}
        {locationSearchActive && userLocation && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <Badge variant="secondary" className="px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <MapPin className="w-4 h-4 mr-2" />
              Showing trainers within {searchDistance === 0 ? 'any distance' : `${searchDistance} km`} of {userLocation.city || userLocation.name || 'your location'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearLocationSearch}
              className="text-gray-500 hover:text-red-500"
            >
              Clear filter
            </Button>
          </div>
        )}
      </div>

      {/* Advanced Filter Modal */}
      <AdvancedFilterModal 
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={applyAdvancedFilters}
        initialFilters={appliedFilters}
        sportCategories={sportCategories}
      />

      {/* Trainer Feedback Banner */}
      {user && user.role === 'trainer' && pendingFeedback && showFeedbackBanner && (
        <div className="max-w-7xl mx-auto px-4 pt-6">
          <Alert className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">📝</span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-orange-900 mb-2">
                  Admin Feedback: Profile Updates Needed
                </h3>
                <AlertDescription className="text-orange-800 space-y-3">
                  <p className="font-semibold">
                    Our admin team has reviewed your profile and requests the following updates:
                  </p>
                  
                  {pendingFeedback.missing_items && pendingFeedback.missing_items.length > 0 && (
                    <div className="bg-white dark:bg-gray-800/50 p-3 rounded-lg border border-orange-200">
                      <p className="font-semibold text-orange-900 mb-2">Missing or Incomplete Items:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {pendingFeedback.missing_items.map((item, idx) => (
                          <li key={item} className="text-orange-800">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {pendingFeedback.additional_notes && (
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-1">Additional Notes:</p>
                      <p className="text-blue-800 whitespace-pre-wrap">{pendingFeedback.additional_notes}</p>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => navigate('/trainer/dashboard')}
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                    >
                      ✏️ Edit Profile
                    </Button>
                    <Button
                      onClick={() => setShowFeedbackBanner(false)}
                      variant="outline"
                      className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      Dismiss
                    </Button>
                  </div>
                </AlertDescription>
              </div>
            </div>
          </Alert>
        </div>
      )}

      {/* Sport Categories */}
      <div className="border-t-2 border-red-100 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md sticky top-[64px] sm:top-[72px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2">
            {/* Left Arrow Button */}
            <button
              onClick={() => {
                const container = document.getElementById('sport-categories-container');
                if (container) {
                  const scrollAmount = 250;
                  const start = container.scrollLeft;
                  const target = Math.max(0, start - scrollAmount);
                  const duration = 400;
                  const startTime = performance.now();
                  
                  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
                  
                  const animateScroll = (currentTime) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeProgress = easeOutCubic(progress);
                    
                    container.scrollLeft = start + (target - start) * easeProgress;
                    
                    if (progress < 1) {
                      requestAnimationFrame(animateScroll);
                    }
                  };
                  
                  requestAnimationFrame(animateScroll);
                }
              }}
              className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-md rounded-full transition-all active:scale-95"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            
            {/* Categories Container */}
            <div 
              id="sport-categories-container"
              className="flex-1 flex items-center gap-3 sm:gap-6 overflow-x-auto sport-categories-scroll"
            >
              {sportCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCategory(selectedCategory === category.name ? null : category.name);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  className={`flex flex-col items-center gap-1 min-w-fit transition-all touch-manipulation select-none ${
                    selectedCategory === category.name 
                      ? 'text-red-600 dark:text-red-400' 
                      : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400'
                  }`}
                >
                  <div className={`text-xl sm:text-2xl p-2 sm:p-3 rounded-full transition-all duration-200 pointer-events-none ${
                    selectedCategory === category.name 
                      ? 'bg-gradient-to-br from-red-200 to-red-800/20 dark:from-red-900 dark:to-red-800 shadow-lg ring-2 ring-red-400 dark:ring-red-600' 
                      : 'hover:bg-red-50 dark:hover:bg-gray-700 hover:shadow-md active:shadow-sm'
                  }`}>
                    {category.icon}
                  </div>
                  <span className={`text-xs font-semibold whitespace-nowrap transition-all pointer-events-none ${
                    selectedCategory === category.name 
                      ? 'font-bold' 
                      : ''
                  }`}>{category.name}</span>
                </button>
              ))}
            </div>
            
            {/* Right Arrow Button */}
            <button
              onClick={() => {
                const container = document.getElementById('sport-categories-container');
                if (container) {
                  const scrollAmount = 250;
                  const start = container.scrollLeft;
                  const maxScroll = container.scrollWidth - container.clientWidth;
                  const target = Math.min(maxScroll, start + scrollAmount);
                  const duration = 400;
                  const startTime = performance.now();
                  
                  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
                  
                  const animateScroll = (currentTime) => {
                    const elapsed = currentTime - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeProgress = easeOutCubic(progress);
                    
                    container.scrollLeft = start + (target - start) * easeProgress;
                    
                    if (progress < 1) {
                      requestAnimationFrame(animateScroll);
                    }
                  };
                  
                  requestAnimationFrame(animateScroll);
                }
              }}
              className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-md rounded-full transition-all active:scale-95"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Banner Advertisement - Full width above trainer grid */}
      <HomepageBannerAd />

      {/* Trainers Grid with Sidebar Layout */}
      <main ref={trainersGridRef} className="max-w-[1600px] mx-auto px-3 sm:px-4 pt-4 sm:pt-5 pb-6 sm:pb-8 scroll-mt-20">
        <div className="relative">
          {/* Main Trainers Grid */}
          <div className="xl:mr-[380px]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {paginatedTrainers.map((trainer, index) => {
                const currentIndex = currentImageIndex[trainer.id] || 0;
                const hasPhotos = trainer.photos && trainer.photos.length > 0;
                const hasMultiplePhotos = hasPhotos && trainer.photos.length > 1;
                const placeholderImage = '/trainer-placeholder-logo-t.png';
                
                // Check if this trainer is highlighted
                const trainerId = String(trainer.id);
                const isHighlighted = Array.isArray(highlightedTrainerId) 
                  ? highlightedTrainerId.includes(trainerId)
                  : highlightedTrainerId === trainerId;

                return (
                  <Card 
                    key={trainer.id}
                    id={`trainer-${trainerId}`}
                    className={`overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm ${
                      isHighlighted
                        ? 'border-red-600 shadow-2xl ring-4 ring-red-300 dark:ring-red-500 scale-105'
                      : 'border-red-300 hover:border-red-400'
                  }`}
                >
                <CardContent className="p-0">
                  {/* Image Carousel */}
                  <Link to={`/trainer/${trainer.id}`} className="block">
                    <div className="relative aspect-[3/2] bg-gradient-to-br from-gray-200 to-red-200 group overflow-hidden">
                      {/* Membership Badges - Dynamic from Boost Categories */}
                      {trainer.membership_active && (
                        (() => {
                          const categoryKey = trainer.membership_type || 'boosted';
                          const category = boostCategories[categoryKey];
                          const IconComponent = category?.icon ? BoostIconMap[category.icon] : (categoryKey === 'boosted' ? Crown : CheckCircle);
                          const bgColor = category?.color || (categoryKey === 'boosted' ? '#EAB308' : '#3B82F6');
                          const badgeText = category?.badge_text || category?.label || (categoryKey === 'boosted' ? 'PREMIUM BOOSTED' : 'FEATURED');
                          const showIcon = category?.show_icon !== false;
                          const showText = category?.show_text !== false;
                          const badgeSize = category?.badge_size || 'medium';
                          const fontSize = category?.font_size || 'xs';
                          const iconSize = category?.icon_size || 'small';
                          const position = category?.position || 'top-left';
                          
                          // Position classes
                          const positionClasses = {
                            'top-left': 'top-2 left-2',
                            'top-right': 'top-2 right-2',
                            'bottom-left': 'bottom-2 left-2',
                            'bottom-right': 'bottom-2 right-2'
                          };
                          
                          // Size classes
                          const sizeClasses = {
                            small: 'px-1.5 py-0.5',
                            medium: 'px-2 py-1',
                            large: 'px-3 py-1.5'
                          };
                          
                          const fontClasses = {
                            xs: 'text-xs',
                            sm: 'text-sm',
                            base: 'text-base'
                          };
                          
                          const iconClasses = {
                            small: 'w-3 h-3',
                            medium: 'w-4 h-4',
                            large: 'w-5 h-5'
                          };
                          
                          return (
                            <div className={`absolute ${positionClasses[position]} z-10`}>
                              <Badge 
                                className={`text-white font-bold shadow-lg border border-white/50 ${sizeClasses[badgeSize]} ${fontClasses[fontSize]} flex items-center gap-1`}
                                style={{ backgroundColor: bgColor }}
                              >
                                {showIcon && IconComponent && <IconComponent className={iconClasses[iconSize]} />}
                                {showText && <span>{badgeText.toUpperCase()}</span>}
                              </Badge>
                            </div>
                          );
                        })()
                      )}

                      {/* Achievement Badges - Dynamic settings from admin */}
                      {trainer.achievement_badges && trainer.achievement_badges.length > 0 && (
                        <div className="absolute top-2 right-2 z-10 flex flex-col gap-0.5 items-end">
                          {trainer.achievement_badges.map((badge) => {
                            const BadgeIcon = BoostIconMap[badge.icon] || Trophy;
                            const showIcon = badge.show_icon !== false;
                            const showText = badge.show_text !== false;
                            const badgeSize = badge.badge_size || 'medium';
                            const fontSize = badge.font_size || 'xs';
                            const iconSize = badge.icon_size || 'small';
                            
                            // Size classes - reduced for smaller badges
                            const sizeClasses = {
                              small: 'px-1 py-0.5',
                              medium: 'px-1.5 py-0.5',
                              large: 'px-2 py-0.5'
                            };
                            
                            const fontClasses = {
                              xs: 'text-[9px]',
                              sm: 'text-[10px]',
                              base: 'text-xs'
                            };
                            
                            const iconClasses = {
                              small: 'w-2 h-2',
                              medium: 'w-2.5 h-2.5',
                              large: 'w-3 h-3'
                            };
                            
                            return (
                              <Badge 
                                key={badge.key}
                                className={`text-white font-semibold shadow-md border border-white/30 flex items-center gap-0.5 ${sizeClasses[badgeSize]} ${fontClasses[fontSize]}`}
                                style={{ backgroundColor: badge.color }}
                                title={badge.name}
                              >
                                {showIcon && <BadgeIcon className={iconClasses[iconSize]} />}
                                {showText && <span>{badge.badge_text}</span>}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                      <img
                        key={`${trainer.id}-${currentIndex}`}
                        src={hasPhotos ? trainer.photos[currentIndex] : placeholderImage}
                        alt={trainer.name}
                        className={`w-full h-full cursor-pointer transition-opacity duration-300 ease-in-out ${
                          hasPhotos ? 'object-cover' : 'object-contain p-2'
                        }`}
                        style={{ animation: 'fadeIn 0.3s ease-in-out' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = placeholderImage;
                          // Change to contain when placeholder is used
                          e.target.className = e.target.className.replace('object-cover', 'object-contain p-2');
                        }}
                      />
                      
                      {hasMultiplePhotos && (
                        <>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              prevImage(trainer.id, trainer.photos.length);
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-800 hover:from-red-600 hover:to-red-800 active:from-red-700 active:to-red-800 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all shadow-lg hover:shadow-xl active:shadow-md text-white z-10"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              nextImage(trainer.id, trainer.photos.length);
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-800 hover:from-red-600 hover:to-red-800 active:from-red-700 active:to-red-800 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all shadow-lg hover:shadow-xl active:shadow-md text-white z-10"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>

                          {/* Dots Indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                            {trainer.photos.map((photo, idx) => (
                              <div
                                key={photo}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  idx === currentIndex ? 'bg-red-500 w-6 shadow-lg' : 'bg-white/60'
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </Link>

                  {/* Trainer Info */}
                  <div className="p-3 sm:p-4 bg-gradient-to-br from-white to-red-50/30 dark:from-gray-800 dark:to-red-800/30">
                    <div className="flex flex-col gap-1 mb-2">
                      {/* Price Row */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold">
                          <span className="font-bold text-red-600 dark:text-red-400">${trainer.pricePerHour}</span>/hr
                        </span>
                        {trainer.profile_views !== undefined && (
                          <div className="flex items-center gap-1 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-full">
                            <Eye className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                            <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">{trainer.profile_views}</span>
                          </div>
                        )}
                      </div>
                      {/* Rating Row */}
                      <div className="flex items-center">
                        <StarRating 
                          rating={trainer.rating || 0} 
                          reviewCount={trainer.review_count || 0}
                          size="sm"
                        />
                      </div>
                    </div>

                    <Link to={`/trainer/${trainer.id}`}>
                      <h3 className="font-bold text-base sm:text-lg mb-1 bg-gradient-to-r from-red-600 to-red-800 dark:from-red-400 dark:to-red-800 bg-clip-text text-transparent hover:from-red-700 hover:to-red-800 transition-all flex items-center gap-1">
                        {trainer.name}
                        {trainer.membership_active && (() => {
                          const categoryKey = trainer.membership_type || 'boosted';
                          const category = boostCategories[categoryKey];
                          const IconComponent = category?.icon ? BoostIconMap[category.icon] : (categoryKey === 'boosted' ? Crown : Star);
                          const iconColor = category?.color || (categoryKey === 'boosted' ? '#EAB308' : '#3B82F6');
                          const title = category?.label || (categoryKey === 'boosted' ? 'Premium Boosted Member' : 'Featured Member');
                          const isBoosted = categoryKey === 'boosted';
                          
                          return IconComponent ? (
                            <IconComponent 
                              className={`w-5 h-5 ${isBoosted ? 'animate-pulse' : ''}`} 
                              style={{ color: iconColor }}
                              title={title}
                            />
                          ) : null;
                        })()}
                      </h3>
                    </Link>

                    <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium flex items-center gap-1 flex-wrap">
                      {trainer.specializations.map((spec, idx) => {
                        const icon = proficiencyIcons[spec.toLowerCase()];
                        return (
                          <span key={spec} className="flex items-center gap-1">
                            {icon && <span className="text-base">{icon}</span>}
                            <span>{spec}</span>
                            {idx < trainer.specializations.length - 1 && <span className="mx-1">•</span>}
                          </span>
                        );
                      })}
                    </p>

                    <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      <span className="text-red-500">📍</span>
                      {trainer.location.province} - {trainer.location.city}
                      {trainer.distance_km !== null && trainer.distance_km !== undefined && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-[10px] font-semibold">
                          {trainer.distance_km} km away
                        </span>
                      )}
                    </p>

                    {/* Availability Summary */}
                    {trainer.availability_summary && trainer.availability_summary.days_display !== 'Not set' && (
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full font-medium">
                          🗓️ {trainer.availability_summary.days_display}
                        </span>
                        {trainer.availability_summary.time_range !== 'Not set' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-full font-medium">
                            ⏰ {trainer.availability_summary.time_range}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Training Types & Gender & Age */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      {/* Training Types */}
                      {trainer.training_types && trainer.training_types.length > 0 && (
                        trainer.training_types.map((type, idx) => (
                          <span
                            key={type}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium"
                          >
                            {type === 'Online' && '🌐'}
                            {type === 'In Person' && '🏋️'}
                            {type === 'Both' && '🌐🏋️'}
                            <span>{type}</span>
                          </span>
                        ))
                      )}
                      
                      {/* Gender with text */}
                      {trainer.gender && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                          {trainer.gender === 'male' && '♂️'}
                          {trainer.gender === 'female' && '♀️'}
                          {trainer.gender === 'non-binary' && '⚧️'}
                          <span className="capitalize">{trainer.gender}</span>
                        </span>
                      )}
                      
                      {/* Age with cake icon */}
                      {trainer.age && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                          <Cake className="w-3 h-3" />
                          <span>{trainer.age} years old</span>
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
            </div>

            {/* Pagination */}
            {!loading && filteredTrainers.length > trainersPerPage && (
              <div className="flex justify-center items-center gap-2 mt-8 mb-4">
                {/* Previous button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    const showPage = page === 1 || 
                                    page === totalPages || 
                                    Math.abs(page - currentPage) <= 1;
                    const showEllipsis = page === 2 && currentPage > 3 || 
                                        page === totalPages - 1 && currentPage < totalPages - 2;
                    
                    if (!showPage && !showEllipsis) return null;
                    
                    if (showEllipsis && !showPage) {
                      return <span key={page} className="px-2 text-gray-400">...</span>;
                    }
                    
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 ${currentPage === page ? 'bg-red-600 hover:bg-red-700 text-white' : ''}`}
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>

                {/* Next button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>

                {/* Page info */}
                <span className="ml-4 text-sm text-gray-500 dark:text-gray-400">
                  Page {currentPage} of {totalPages} ({filteredTrainers.length} trainers)
                </span>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">Loading trainers...</p>
              </div>
            )}

            {!loading && fetchError && (
              <div className="text-center py-12" data-testid="trainers-fetch-error">
                <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">We couldn't load trainers right now. Please check your connection and try again.</p>
                <Button
                  data-testid="trainers-retry-button"
                  onClick={() => fetchTrainers(locationSearchActive && userLocation ? { lat: userLocation.lat, lng: userLocation.lng, distance: searchDistance } : null)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Try Again
                </Button>
              </div>
            )}

            {!loading && !fetchError && filteredTrainers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">No trainers found matching your criteria.</p>
              </div>
            )}

            {/* Mobile Advertisements - Show below trainers on small screens */}
            <div className="xl:hidden mt-8">
              <HomepageAdSidebar isMobile={true} />
            </div>
          </div>

          {/* Right Sidebar - Advertisements (Desktop only) - Centered in available space */}
          <div className="hidden xl:block absolute top-0" style={{ left: 'calc(100% - 380px + 50px)', width: '280px' }}>
            <div className="sticky top-28">
              <HomepageAdSidebar />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;